
import { defineComponent } from 'vue';
import { useStore } from 'vuex';
import { computed, markRaw } from '@vue/runtime-core';
import AppHeader from '@/components/2.6/Header.vue';
import AppFooter from '@/components/2.6/Footer.vue';
import CompTabs from '@/components/2.6/CompTabs.vue';
import DataGrid from '@/components/2.6/DataGrid.vue';
import Guide from '@/components/2.6/Guide.vue';
import { userSettingsStore, Getters } from '@/store';
import JumpStart from '@/components/2.6/modals/JumpStart.vue';
import LaunchUserTutorialIntro from '@/components/2.6/modals/LaunchUserTutorialIntro.vue';
import MLSImportModal from '@/components/2.6/modals/MLSImport.vue';
import MLSImportPreviouslyViewed from '@/components/2.6/modals/MLSImportPreviouslyViewed.vue';
import UnVisitedComps from '@/components/2.6/modals/UnVisitedComps.vue';
import utils from '@/utils/QSUtils';
import Loader from '@/components/Loader.vue';
import ModalConfirmation from '@/components/modals/Confirmation.vue';
import HostExchangeManager from '@/manager/2.6/HostExchangeManager';
import TutorialManager from '@/manager/TutorialManager';
import _ from 'lodash';
import GuideManager from '@/manager/GuideManager';
import LogUsageManager from '@/manager/LogUsageManager';
import IMlsImportStats from '@/models/mls/IMlsImportStats';
import IComps from '@/models/comp/IComps';
import IMlsPreviouslyViewed from '@/models/mls/IMlsPreviouslyViewed';
import PhotoView from '@/components/2.6/modals/PhotoView.vue';
import { makeGettersWatchable } from '@/utils/StoreWatch';
import MlsTokenManager from '@/manager/2.6/MlsTokenManager';
import MlsImportManager from '@/manager/2.6/MlsImportManager';
import CompsManager from '@/manager/2.6/CompsManager';
import QSUtils from '@/utils/QSUtils';

export default defineComponent({
    components: {
        AppHeader,
        AppFooter,
        Guide,
        CompTabs,
        DataGrid,
        JumpStart,
        LaunchUserTutorialIntro,
        MLSImportModal,
        MLSImportPreviouslyViewed,
        Loader,
        ModalConfirmation,
        UnVisitedComps,
        PhotoView,
    },
    emits: ['resizeWindowGrid', 'launchUserTutorialIntro'],
    setup() {
        const store = useStore();
        const userSettings = makeGettersWatchable(userSettingsStore);

        return { store, userSettings };
    },
    data() {
        return {
            guideIsvisible: 'notSet',
            hasComps: false,
            launchUserTutorialIntro: {
                show: false,
                component: markRaw(LaunchUserTutorialIntro),
            },
            modalJumpStart: {
                show: false,
                component: markRaw(JumpStart),
            },
            modalUnVisitedComps: {
                show: false,
                comps: [] as IComps[],
                component: markRaw(UnVisitedComps),
            },
            modalMLSImport: {
                show: false,
                component: markRaw(MLSImportModal),
            },
            modalMLSImportPreviouslyViewed: {
                show: false,
                previouslyVieweds: [] as IMlsPreviouslyViewed[],
                component: markRaw(MLSImportPreviouslyViewed),
            },
            modalPhotoView: {
                show: false,
                component: markRaw(PhotoView),
            },
            modalConfirmation: {
                show: false,
                component: markRaw(ModalConfirmation),
                title: '',
                showModalHeader: true,
                message: '',
                size: '',
                buttons: [] as any,
            },
            isSaving: false,
        };
    },
    async mounted() {
        (this as any).eventBus.on('modalPhotoView', (e: any) => {
            this.modalPhotoView.show = true;
        });
        (this as any).eventBus.on('launchUserTutorialIntro', (e: any) => {
            this.launchUserTutorialIntro.show = true;
        });
        (this as any).eventBus.on('modalJumpStart', (e: any) => {
            this.modalJumpStart.show = true;
        });
        (this as any).eventBus.on('modalMLSImportAllErrors', (e: any) => {
            const importStats: IMlsImportStats = e.importStats;
            const isPlural = importStats.errors > 1 ? 'comps' : 'comp';
            this.modalConfirmation.show = true;
            this.modalConfirmation.title = 'No MLS data was imported';
            this.modalConfirmation.message = `QuickSource failed to import <b>${importStats.errors}</b> ${isPlural}.</p><p>A minimum of both <b>street address</b> and <b>zipcode</b> are required for each comp to be imported.`;
            this.modalConfirmation.buttons = [
                {
                    label: 'Close',
                    css: 'btn-primary',
                    callback: () => {
                        this.modalChecker();
                    },
                },
            ];
        });
        (this as any).eventBus.on('modalMLSImport', async (e: any) => {
            await this.launchMlsImport();
        });
        (this as any).eventBus.on('modalMLSImportPreviouslyViewed', async (e: any) => {
            await this.launchMlsImportPreviouslyViewed(e.previouslyVieweds);
        });
        (this as any).eventBus.on('saveAndClose', async (e: any) => {
            await this.saveAndClose();
        });
        (this as any).eventBus.on('checkTabOverflow', async (e: any) => {
            this.checkTabOverflow();
        });
        (this as any).eventBus.on('launchUserTutorial', (e: any) => {
            TutorialManager.showTutorial(1, this.userSettings.UserTutorialCompleted);
        });
        (this as any).eventBus.on('toggleGuide', async (e: any) => {
            if (!e.isvisible) {
                await this.hideGuide();
            } else {
                await this.showGuide();
            }
            (this as any).eventBus.emit('resizeWindowGrid');
        });
        (this as any).eventBus.on('saveAndCloseFinal', async (e: any) => {
            await this.saveAndCloseFinal();
        });

        await this.modalChecker();

        LogUsageManager.loadedDiscrepancy();
    },
    created() {
        this.store.watch(
            (state, getters) => getters[Getters.CompAddresses.HasComps],
            async () => {
                const result = computed(() => this.store.getters[Getters.CompAddresses.HasComps]);
                this.hasComps = result.value;
                this.modalChecker();
            }
        );
        this.userSettings.watch('DisplayProductGuide', async () => {
            const result = computed(() => this.userSettings.DisplayProductGuide);
            this.guideIsvisible = result.value ? 'show' : 'hide';
        });
    },

    methods: {
        async modalChecker() {
            if (!this.checkHasComps()) {
                this.modalJumpStart.show = true;
                // The user tutorial should be the last thing to run
            } else if (!this.userSettings.UserTutorialCompleted) {
                await utils.sleep(300);
                (this as any).eventBus.emit('launchUserTutorialIntro');
            }
        },
        checkHasComps() {
            const computedHasComps = computed(() => this.store.getters[Getters.CompAddresses.HasComps]);
            if (computedHasComps.value) {
                this.hasComps = computedHasComps.value;
            }
            return computedHasComps.value;
        },
        async launchMlsImport() {
            const mlsToken = await MlsTokenManager.getToken(false);
            if (mlsToken) {
                this.modalMLSImport.show = true;
            } else {
                this.modalConfirmation.show = true;
                this.modalConfirmation.showModalHeader = false;
                this.modalConfirmation.title = 'MLS Import Error';
                this.modalConfirmation.message = `QuickSource has experienced an error, please try again later. We're sorry for the inconvenience.`;
                this.modalConfirmation.buttons = [
                    {
                        label: 'Try again',
                        css: 'btn-primary',
                        callback: async () => {
                            await MlsTokenManager.getToken(true);
                            this.launchMlsImport();
                        },
                    },
                    {
                        label: 'Close',
                        css: 'btn btn-outline-secondary',
                        callback: () => {
                            // Need to check if there are any comps.  If not, then relaunch the Jump Start modal
                            if (MlsImportManager.relaunchJumpStartModal()) {
                                this.modalJumpStart.show = true;
                            }
                        },
                    },
                ];
            }
        },
        async launchMlsImportPreviouslyViewed(previouslyVieweds: IMlsPreviouslyViewed[]) {
            this.modalMLSImportPreviouslyViewed.show = true;
            this.modalMLSImportPreviouslyViewed.previouslyVieweds = previouslyVieweds;
        },
        saveAndClose() {
            if(QSUtils.debugger()) {
                console.log("saveAndClose method");
            }

            // Check to see if any comps are still loading.  If so, then return
            if (CompsManager.compsLoading()) {
                return;
            }

            const exportAllComps = computed(() => this.userSettings.ExportAllComps);
            if(QSUtils.debugger()) {
                console.log("exportAllComps result: ", exportAllComps);
            }

            if (!exportAllComps.value) {
                // Check if all comps have been visited
                const allComps = computed(() => this.store.getters[Getters.CompAddresses.Comps]);
                const unVisitedComps: IComps[] = allComps.value.filter((obj: { hasVisited: boolean }) => !obj.hasVisited);
                if (unVisitedComps.length > 0) {
                    this.modalUnVisitedComps.show = true;
                    this.modalUnVisitedComps.comps = unVisitedComps;
                    return;
                }
            }

            this.saveAndCloseFinal();
        },
        async saveAndCloseFinal() {
            if(QSUtils.debugger()) {
                console.log("saveAndCloseFinal method");
            }

            GuideManager.guideCompleted(7);

            this.isSaving = true;
            await HostExchangeManager.sendAll();
            if(QSUtils.debugger()) {
                console.log("sendAll completed");
                console.log("APPLICATION WILL NOT CLOSE IN DEBUG MODE!");
            }

            if(!QSUtils.debugger()) {
                console.log("APPLICATION CLOSE")
                await HostExchangeManager.closeApp(true);
            }
            this.isSaving = false;
        },
        checkTabOverflow() {
            const compContainer = document.getElementById('compContainer') as HTMLDivElement;
            const tabNavLeft = document.getElementsByClassName('tab-navigation-left')[0] as HTMLDivElement;
            const tabNavRight = document.getElementsByClassName('tab-navigation-right')[0] as HTMLDivElement;

            if (compContainer !== null) {
                if (utils.checkOverflowX(compContainer)) {
                    tabNavLeft.style.display = 'block';
                    tabNavRight.style.display = 'block';
                } else {
                    tabNavLeft.style.display = 'none';
                    tabNavRight.style.display = 'none';
                }
            }
        },
        async hideGuide() {
            this.guideIsvisible = 'hide';
            await this.userSettings.setDisplayProductGuide({
                DisplayProductGuide: false,
            });
        },
        async showGuide() {
            this.guideIsvisible = 'show';
            await this.userSettings.setDisplayProductGuide({
                DisplayProductGuide: true,
            });
        },
    },
});
